<template>
    <!-- Fenêtre d'upload de fichiers : non fermable pendant l'envoi -->
    <b-modal
        :visible="visible"
        :title="$t('UploadTitle')"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
        size="lg">
        <!-- Progression globale de l'envoi -->
        <div class="d-flex justify-content-between mb-1">
            <span>{{ sentCount }} / {{ safeFiles.length }}</span>
            <span :class="'text-' + Variant" class="font-weight-bold">{{ progress }} %</span>
        </div>
        <b-progress
            :value="progress"
            max="100"
            animated
            :variant="Variant"
            height="1.25rem"
            class="mb-5" />
        <!-- Liste des fichiers et de leur statut -->
        <div ref="scroll" class="list-scroll">
        <b-list-group>
            <b-list-group-item
                v-for="(file, index) in safeFiles"
                :key="index"
                class="d-flex justify-content-between align-items-center py-2 px-3" style="border-top-right-radius: 0; border-bottom-right-radius: 0;">
                <div class="d-flex align-items-center overflow-hidden mr-2">
                    <font-awesome-icon
                        :icon="statusIcon(file.Status)"
                        class="mr-2"
                        :class="statusClass(file.Status)"
                        style="width: 18px;" />
                    <span class="text-truncate">
                        {{ file.Name }}
                        <small class="text-muted ml-1">({{ formatSize(file.Size) }})</small>
                    </span>
                </div>
                <div class="d-flex align-items-center flex-shrink-0">
                    <b-badge class="px-2 font-weight-semibold" pill  :variant="statusVariant(file.Status)" style="font-size: xx-small;">{{ $t(statusLabel(file.Status)) }}</b-badge>
                    <b-button
                        v-if="file.Status === 'Failed' && !uploadInProgress"
                        variant="link"
                        :class="'text-' + Variant"
                        class="p-0 ml-2"
                        v-b-tooltip.hover
                        :title="$t('Retry')"
                        @click="retry(index)">
                        <font-awesome-icon icon="fa-solid fa-arrows-rotate" />
                    </b-button>
                </div>
            </b-list-group-item>
        </b-list-group>
        </div>

        <!-- Bouton de fermeture de la fenêtre (pill, désactivé pendant l'upload) -->
        <template #modal-footer>
            <b-button pill :variant="Variant" :disabled="uploadInProgress" @click="onClose">
                {{ $t('Finish') }}
            </b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import UploadStore, { UploadFileStatus } from '@/store/UploadStore';
import WorkingModeEnum from '@/enums/WorkingModeEnum';

/**
 * Fenêtre de suivi de l'upload de fichiers :
 * progression globale en pourcentage et liste des fichiers avec leur statut.
 * Composant générique piloté par le store "UploadStore", monté au niveau de l'application.
 * La fenêtre ne peut pas être fermée pendant l'upload.
 */
@Component
export default class UploadProgressDialog extends Vue {

    private beforeDestroy(): void {
        if (!this.$store.hasModule('UploadStore')) {
            this.$store.unregisterModule('UploadStore');
        }
    }

    private beforeCreate(): void {
        if (!this.$store.hasModule('UploadStore')) {
            this.$store.registerModule('UploadStore', UploadStore);
        }
    }

    /**
     * Liste des fichiers de la fenêtre d'upload
     */
    @Getter('UploadStore/GetFiles')
    private files!: { Name: string, Size: number, Status: UploadFileStatus }[];

    /**
     * Visibilité de la fenêtre
     */
    @Getter('UploadStore/GetDialogVisible')
    private visible!: boolean;

    /**
     * Progression globale de l'upload (0-100)
     */
    @Getter('UploadStore/GetProgress')
    private progress!: number;

    /**
     * Indique si un upload est en cours
     */
    @Getter('UploadStore/GetInProgress')
    private uploadInProgress!: boolean;

    /**
     * Index du fichier suivi par la liste (premier fichier en cours, sinon premier échec)
     */
    @Getter('UploadStore/GetCurrentIndex')
    private currentIndex!: number;

    /**
     * Variant de couleur du mode de travail courant.
     * Le composant est monté au niveau de l'application : le Variant fourni par les pages
     * n'est pas injectable ici, il est déduit du mode de travail courant du store global.
     */
    public get Variant(): string {
        switch (this.$store.getters['GetWorkingMode']) {
            case WorkingModeEnum.Workbench:
                return 'secondary';
            case WorkingModeEnum.Map:
                return 'tertiary';
            case WorkingModeEnum.Administration:
                return 'quartary';
            default:
                return 'primary';
        }
    }

    /**
     * Liste des fichiers affichée par le template
     * (garde contre une evaluation anterieure a l'enregistrement du store)
     */
    private get safeFiles(): { Name: string, Size: number, Status: UploadFileStatus }[] {
        return this.files ?? [];
    }

    /**
     * Nombre de fichiers envoyés
     */
    public get sentCount(): number {
        return this.safeFiles.filter(file => file.Status === 'Sent').length;
    }

    /**
     * Indique si tous les fichiers ont été envoyés avec succès
     */
    private get allSent(): boolean {
        return this.safeFiles.length > 0 && this.safeFiles.every(file => file.Status === 'Sent');
    }

    /**
     * Nouvelle tentative d'envoi d'un fichier en échec
     * @param index Index du fichier dans la liste
     */
    private retry(index: number): void {
        this.$store.dispatch('UploadStore/RetryUploadFile', { Index: index });
    }

    /**
     * Fermeture de la fenêtre (désactivée pendant l'upload)
     */
    private onClose(): void {
        this.$store.dispatch('UploadStore/CloseUploadDialog');
    }

    /**
     * Dernier index suivi (evite de relancer un scroll identique a chaque re-render)
     */
    private lastScrolledIndex: number = -1;

    /**
     * Suivi de l'envoi : la liste défile vers le fichier en cours
     */
    @Watch('currentIndex')
    private onCurrentIndexChanged(): void {
        this.followUpload();
    }

    /**
     * Redondance volontaire : la mutation des statuts declenche aussi le suivi
     */
    @Watch('files', { deep: true })
    private onFilesChanged(): void {
        this.followUpload();
    }

    /**
     * Reinitialisation du suivi a l'ouverture de la fenetre
     */
    @Watch('visible')
    private onVisibleChanged(value: boolean): void {
        if (value) {
            this.lastScrolledIndex = -1;
        }
    }

    /**
     * Appele a chaque re-rendu du composant (progression, statuts) :
     * declencheur le plus fiable pour suivre l'envoi en cours
     */
    private updated(): void {
        this.followUpload();
    }

    /**
     * Fait defiler la liste vers le fichier en cours si necessaire
     */
    private followUpload(): void {
        if (!this.visible || this.currentIndex === this.lastScrolledIndex) {
            return;
        }

        this.lastScrolledIndex = this.currentIndex;
        this.$nextTick(() => this.scrollToIndex(this.currentIndex));
    }

    /**
     * Fait défiler la liste pour centrer le fichier à l'index donné dans la zone visible.
     * Calcul par getBoundingClientRect : independant de la hierarchie de position du modal.
     * @param index Index du fichier dans la liste
     */
    private scrollToIndex(index: number): void {
        const container = this.$refs.scroll as HTMLElement | undefined;

        if (!container || index < 0) {
            return;
        }

        const items = container.querySelectorAll('.list-group-item');
        const item = items[index] as HTMLElement | undefined;

        if (!item) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        // Decalage de la ligne par rapport au haut visible du conteneur, puis recentrage
        const delta = itemRect.top - containerRect.top;
        const target = container.scrollTop + delta - (container.clientHeight - item.clientHeight) / 2;

        container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }

    /**
     * Icône FontAwesome associée à un statut d'envoi
     * @param status Statut du fichier
     */
    private statusIcon(status: UploadFileStatus): string {
        switch (status) {
            case 'Sent':
                return 'fa-solid fa-check-circle';
            case 'Uploading':
                return 'fa-solid fa-arrows-rotate';
            case 'Failed':
                return 'fa-solid fa-triangle-exclamation';
            default:
                return 'fa-solid fa-clock-rotate-left';
        }
    }

    /**
     * Classe CSS associée à un statut d'envoi
     * @param status Statut du fichier
     */
    private statusClass(status: UploadFileStatus): string {
        switch (status) {
            case 'Sent':
                return 'text-success';
            case 'Uploading':
                return 'text-info fa-spin';
            case 'Failed':
                return 'text-danger';
            default:
                return 'text-muted';
        }
    }

    /**
     * Variante de badge associée à un statut d'envoi
     * @param status Statut du fichier
     */
    private statusVariant(status: UploadFileStatus): string {
        switch (status) {
            case 'Sent':
                return 'success';
            case 'Uploading':
                return 'info';
            case 'Failed':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    /**
     * Clé de traduction du libellé d'un statut d'envoi
     * @param status Statut du fichier
     */
    private statusLabel(status: UploadFileStatus): string {
        switch (status) {
            case 'Sent':
                return 'StatusSent';
            case 'Uploading':
                return 'StatusUploading';
            case 'Failed':
                return 'StatusFailed';
            default:
                return 'StatusPending';
        }
    }

    /**
     * Formatage d'une taille en octets en Mo
     * @param bytes Taille en octets
     */
    private formatSize(bytes: number): string {
        return (bytes / 1024 / 1024).toFixed(2) + ' Mo';
    }
}
</script>

<style scoped>
.list-scroll {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
}
</style>
